import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'
import { useQuery, type QueryClient } from '@tanstack/vue-query'
import { listOrgExports } from '@/api/assets/exports'

/**
 * Whether an organization has ever exported, for the Exports page's entry and route where
 * `assets.library_exports` is off (`docs/plans/organization-exports.md` E8, E10).
 *
 * The log is read whether or not the flag is on, so that an administrator still sees what left
 * after an operator switched exports off. An organization that never had them would only meet a
 * "not enabled" notice over an empty log, so the page is offered there only once it has a row.
 *
 * One page of one entry, asked **in the organization the key names** — never in whichever one the
 * page's `X-Org-Id` names, which during a switch is already the next organization while the
 * drawer still draws the one being left. It is a query, so it goes with the rest of the cache:
 * the session guard clears it on every change of organization, and every sign-out clears it too.
 * Until then it is never asked again: a log only grows, and an organization exporting today has
 * the flag, which offers the page without this read.
 */
const historyKey = (organizationId: string) => ['settings', 'org-export-history', organizationId] as const

function readHistory(organizationId: string, signal: AbortSignal): Promise<boolean> {
  return listOrgExports({ order: 'desc', limit: 1 }, signal, organizationId).then((page) => page.data.length > 0)
}

/** For the route's gate. A failed read answers no history, as a refused one would. */
export function loadOrgExportHistory(queryClient: QueryClient, organizationId: string): Promise<boolean> {
  return queryClient
    .fetchQuery({
      queryKey: historyKey(organizationId),
      queryFn: ({ signal }) => readHistory(organizationId, signal),
      staleTime: Infinity,
    })
    .catch(() => false)
}

/**
 * For a setup: whether `organizationId` has exported, `false` until the answer lands. Asked only
 * while `enabled` holds, so an organization carrying the flag never reads its log for this.
 */
export function useOrgExportHistory(
  organizationId: MaybeRefOrGetter<string | undefined>,
  enabled: MaybeRefOrGetter<boolean>,
): ComputedRef<boolean> {
  const id = computed(() => toValue(organizationId) ?? '')
  const query = useQuery({
    queryKey: computed(() => historyKey(id.value)),
    // The organization the key names, read off the key: the one the answer is kept under.
    queryFn: ({ queryKey, signal }) => readHistory(String(queryKey[2]), signal),
    staleTime: Infinity,
    enabled: computed(() => id.value !== '' && toValue(enabled)),
  })
  return computed(() => query.data.value === true)
}
